import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Smartphone, Mail, Linkedin, Send, Loader2, CheckCircle2, KeyRound, X } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import SEO, { buildBreadcrumbJsonLd } from "@/components/SEO";
import { seoConfig } from "@/data/seoConfig";

const locations = [
  {
    country: "USA",
    flag: "/assets/usa.png",
    address: "101 Morgan Lane, Suite # 188, Plainsboro NJ 08536",
  },
  {
    country: "CANADA",
    flag: "/assets/canada.png",
    address: "204-2401 Eglinton Ave E,Scarborough ON M1K2N8.",
  },
  {
    country: "INDIA",
    flag: "/assets/flag.png",
    address:
      "MILLENNIUM Tower, 5th Floor, Plot# 10/1, Sector – III, HUDA Techno Enclave, Madhapur, Hyderabad, Telangana – 500081.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

// Dial code + expected national-number length per country, used for the
// country selector and to validate the mobile number before an OTP is sent.
const countries = [
  { iso: "US", name: "United States", dial: "+1", flag: "🇺🇸", length: 10 },
  { iso: "CA", name: "Canada", dial: "+1", flag: "🇨🇦", length: 10 },
  { iso: "IN", name: "India", dial: "+91", flag: "🇮🇳", length: 10 },
  { iso: "GB", name: "United Kingdom", dial: "+44", flag: "🇬🇧", length: 10 },
  { iso: "AU", name: "Australia", dial: "+61", flag: "🇦🇺", length: 9 },
  { iso: "AE", name: "United Arab Emirates", dial: "+971", flag: "🇦🇪", length: 9 },
  { iso: "SG", name: "Singapore", dial: "+65", flag: "🇸🇬", length: 8 },
  { iso: "DE", name: "Germany", dial: "+49", flag: "🇩🇪", length: 10 },
  { iso: "FR", name: "France", dial: "+33", flag: "🇫🇷", length: 9 },
  { iso: "NL", name: "Netherlands", dial: "+31", flag: "🇳🇱", length: 9 },
  { iso: "IE", name: "Ireland", dial: "+353", flag: "🇮🇪", length: 9 },
  { iso: "ES", name: "Spain", dial: "+34", flag: "🇪🇸", length: 9 },
  { iso: "IT", name: "Italy", dial: "+39", flag: "🇮🇹", length: 10 },
  { iso: "PT", name: "Portugal", dial: "+351", flag: "🇵🇹", length: 9 },
  { iso: "CH", name: "Switzerland", dial: "+41", flag: "🇨🇭", length: 9 },
  { iso: "SE", name: "Sweden", dial: "+46", flag: "🇸🇪", length: 9 },
  { iso: "NO", name: "Norway", dial: "+47", flag: "🇳🇴", length: 8 },
  { iso: "DK", name: "Denmark", dial: "+45", flag: "🇩🇰", length: 8 },
  { iso: "PL", name: "Poland", dial: "+48", flag: "🇵🇱", length: 9 },
  { iso: "MX", name: "Mexico", dial: "+52", flag: "🇲🇽", length: 10 },
  { iso: "BR", name: "Brazil", dial: "+55", flag: "🇧🇷", length: 11 },
  { iso: "ZA", name: "South Africa", dial: "+27", flag: "🇿🇦", length: 9 },
  { iso: "SA", name: "Saudi Arabia", dial: "+966", flag: "🇸🇦", length: 9 },
  { iso: "QA", name: "Qatar", dial: "+974", flag: "🇶🇦", length: 8 },
  { iso: "KW", name: "Kuwait", dial: "+965", flag: "🇰🇼", length: 8 },
  { iso: "PH", name: "Philippines", dial: "+63", flag: "🇵🇭", length: 10 },
  { iso: "MY", name: "Malaysia", dial: "+60", flag: "🇲🇾", length: 9 },
  { iso: "ID", name: "Indonesia", dial: "+62", flag: "🇮🇩", length: 10 },
  { iso: "TH", name: "Thailand", dial: "+66", flag: "🇹🇭", length: 9 },
  { iso: "VN", name: "Vietnam", dial: "+84", flag: "🇻🇳", length: 9 },
  { iso: "JP", name: "Japan", dial: "+81", flag: "🇯🇵", length: 10 },
  { iso: "KR", name: "South Korea", dial: "+82", flag: "🇰🇷", length: 10 },
  { iso: "CN", name: "China", dial: "+86", flag: "🇨🇳", length: 11 },
  { iso: "HK", name: "Hong Kong", dial: "+852", flag: "🇭🇰", length: 8 },
  { iso: "NZ", name: "New Zealand", dial: "+64", flag: "🇳🇿", length: 9 },
];

const EMAIL_VERIFY_URL = "https://crmdev.miti.us/api/v1/email/verify-email";
const DEMO_REQUEST_URL = "https://crmdev.miti.us/api/request-demo";
const SEND_OTP_URL = "https://x47jfk2507.execute-api.us-east-1.amazonaws.com/prod/send-otp";

const ContactUs = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const [emailStatus, setEmailStatus] = useState("idle"); // idle | verifying | valid | invalid
  const [emailError, setEmailError] = useState("");

  const [countryIso, setCountryIso] = useState("US");
  const [phoneStatus, setPhoneStatus] = useState("idle"); // idle | sending | pending | verified | error
  const [otpToken, setOtpToken] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const selectedCountry = countries.find((c) => c.iso === countryIso) || countries[0];
  const rawPhone = form.phone.replace(/\D/g, "");
  const phoneReady = rawPhone.length === selectedCountry.length;
  const phoneTouched = rawPhone.length > 0;
  const phoneFormatError =
    phoneTouched && !phoneReady
      ? `Enter a valid ${selectedCountry.length}-digit mobile number for ${selectedCountry.name}.`
      : "";
  const isEmailValid = emailStatus === "valid";
  const isPhoneVerified = phoneStatus === "verified";
  const canSubmit = isEmailValid && isPhoneVerified;

  const update = (key) => (e) => {
    const value = key === "phone" ? e.target.value.replace(/\D/g, "").slice(0, selectedCountry.length) : e.target.value;
    setForm({ ...form, [key]: value });
    if (key === "email") {
      setEmailStatus("idle");
      setEmailError("");
    }
    if (key === "phone") {
      setPhoneStatus("idle");
      setOtpToken("");
    }
  };

  const handleCountryChange = (e) => {
    setCountryIso(e.target.value);
    setForm({ ...form, phone: "" });
    setPhoneStatus("idle");
    setOtpToken("");
  };

  const verifyEmail = async (email) => {
    if (!email) return;
    setEmailStatus("verifying");
    setEmailError("");
    try {
      const res = await fetch(EMAIL_VERIFY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      const result = data?.data?.results?.[0];
      if (result?.is_valid === true) {
        setEmailStatus("valid");
      } else {
        setEmailStatus("invalid");
        setEmailError("Please enter a valid email address.");
      }
    } catch {
      setEmailStatus("invalid");
      setEmailError("Could not verify email. Please try again.");
    }
  };

  useEffect(() => {
    const value = form.email.trim();
    if (!/\S+@\S+\.\S+/.test(value)) {
      setEmailStatus("idle");
      return;
    }
    const timer = setTimeout(() => verifyEmail(value), 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email]);

  const sendOtp = async () => {
    setPhoneStatus("sending");
    try {
      const res = await fetch(SEND_OTP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone_code: selectedCountry.dial, phone: rawPhone }),
      });
      const data = await res.json();
      if (data?.status === true) {
        setOtpToken(data.token);
        setPhoneStatus("pending");
        setOtpValue("");
        setOtpError("");
        setShowOtpModal(true);
      } else {
        setPhoneStatus("error");
        toast.error("Failed to send OTP. Please try again.");
      }
    } catch {
      setPhoneStatus("error");
      toast.error("Could not send OTP. Please try again.");
    }
  };

  const verifyOtp = () => {
    try {
      const decoded = atob(atob(atob(otpToken))).split("").reverse().join("");
      if (decoded.trim() === otpValue.trim()) {
        setPhoneStatus("verified");
        setShowOtpModal(false);
        toast.success("Mobile number verified!");
      } else {
        setOtpError("Incorrect OTP. Please try again.");
      }
    } catch {
      setOtpError("Verification failed. Please request a new OTP.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const requestDatetime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

      const payload = {
        source: "miti.us",
        request_datetime: requestDatetime,
        name: form.name,
        email: form.email,
        phone_code: selectedCountry.dial,
        phone: form.phone,
        subject: form.subject,
        description: form.message,
      };

      const res = await fetch(DEMO_REQUEST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data?.success) {
        toast.success("Message sent! Our team will get back to you shortly.");
        setForm({ name: "", phone: "", email: "", subject: "", message: "" });
        setEmailStatus("idle");
        setCountryIso("US");
        setPhoneStatus("idle");
        setOtpToken("");
      } else {
        toast.error(data?.message || "Failed to send message. Please try again.");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldCls =
    "rounded-lg border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-miti-blue focus:ring-2 focus:ring-miti-blue/15 transition-all";
  const inputCls = `w-full ${fieldCls}`;
  const labelCls = "mb-1.5 block text-xs font-medium text-slate-600";

  return (
    <>
      <SEO {...seoConfig.contact} jsonLd={[buildBreadcrumbJsonLd(seoConfig.contact.crumbs)]} />
      <PageHero title="Contact us" crumbs={[{ label: "Home", href: "/" }, { label: "Contact Us" }]} lightfall />

      <section data-testid="contact-locations" className="pt-24 lg:pt-12 pb-24 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-5">
          {locations.map((loc, i) => (
            <motion.div
              key={loc.country}
              {...fadeUp}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              data-testid={`location-card-${loc.country.toLowerCase()}`}
              className="text-center rounded-2xl border border-slate-200 bg-white p-9 hover:border-miti-blue/40 hover:shadow-xl hover:shadow-miti-blue/5 transition-all duration-300"
            >
              <div className="mx-auto w-14 h-14 rounded-full overflow-hidden border border-slate-200 shadow-sm">
                <img src={loc.flag} alt={`${loc.country} flag`} className="w-full h-full object-cover" />
              </div>
              <h3 className="mt-5 font-heading font-bold text-xl tracking-wide text-slate-900">{loc.country}</h3>
              <p className="mt-3 text-sm md:text-base text-slate-500 leading-relaxed">{loc.address}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section data-testid="contact-form-section" className="pb-24 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">Get In Touch</span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              Don't hesitate to contact us
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              Feel free to contact us with any questions about our products or services. Our team is eager to engage
              in conversations and provide answers to your inquiries.
            </p>
            <div className="mt-8 flex flex-wrap gap-6">
              <a
                href="tel:+16097507120"
                data-testid="contact-phone-link"
                className="flex items-center gap-3 text-sm font-semibold text-slate-800 hover:text-miti-blue transition-colors"
              >
                <span className="w-10 h-10 rounded-full bg-miti-blue/10 text-miti-blue flex items-center justify-center">
                  <Smartphone size={17} />
                </span>
                (+1) 609-750-7120
              </a>
              <a
                href="mailto:info@miti.us"
                data-testid="contact-email-link"
                className="flex items-center gap-3 text-sm font-semibold text-slate-800 hover:text-miti-blue transition-colors"
              >
                <span className="w-10 h-10 rounded-full bg-miti-blue/10 text-miti-blue flex items-center justify-center">
                  <Mail size={17} />
                </span>
                info@miti.us
              </a>
            </div>
            <h3 className="mt-10 font-heading font-semibold text-lg text-slate-900">Follow our social media :</h3>
            <a
              href="https://www.linkedin.com/company/millenniuminfotechusa"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin-link"
              aria-label="Millennium Info Tech on LinkedIn"
              className="mt-4 inline-flex items-center justify-center w-11 h-11 rounded-lg bg-miti-blue text-white hover:bg-[#005a96] hover:scale-105 transition-all"
            >
              <Linkedin size={20} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl border border-slate-200 bg-white p-7 sm:p-9 shadow-xl shadow-slate-900/5"
          >
            <div className="flex items-center gap-4">
              <span className="shrink-0 text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
                Send us a message
              </span>
              <span className="h-px flex-1 bg-slate-200" />
            </div>

            <form onSubmit={submit} className="mt-7 space-y-5" data-testid="contact-form">
              <div>
                <label htmlFor="contact-name-input" className={labelCls}>
                  Full name
                </label>
                <input
                  id="contact-name-input"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  required
                  data-testid="contact-name-input"
                  className={inputCls}
                />
              </div>

              <div>
                <label className={labelCls}>Mobile number</label>
                <div className="flex gap-2">
                  <select
                    value={countryIso}
                    onChange={handleCountryChange}
                    aria-label="Country code"
                    data-testid="contact-country-select"
                    className={`${fieldCls} w-[112px] shrink-0 px-2 text-xs sm:text-sm`}
                  >
                    {countries.map((c) => (
                      <option key={c.iso} value={c.iso}>
                        {c.iso} {c.dial}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="609 750 7120"
                    required
                    maxLength={selectedCountry.length}
                    title={`Enter a valid ${selectedCountry.length}-digit mobile number.`}
                    data-testid="contact-phone-input"
                    className={`${fieldCls} min-w-0 flex-1 ${phoneStatus === "verified" ? "border-emerald-500" : phoneStatus === "error" || phoneFormatError ? "border-red-500" : ""}`}
                  />
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={!phoneReady || phoneStatus === "sending" || phoneStatus === "verified"}
                    data-testid="contact-send-otp-button"
                    className="shrink-0 whitespace-nowrap rounded-lg border border-miti-blue/40 bg-miti-blue/10 px-4 text-xs font-semibold text-miti-blue transition-colors hover:border-miti-blue hover:bg-miti-blue/20 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {phoneStatus === "sending" ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : phoneStatus === "pending" ? (
                      "Resend"
                    ) : (
                      "Verify"
                    )}
                  </button>
                </div>
                {phoneStatus === "verified" && (
                  <p className="mt-1.5 flex items-center gap-1 text-xs text-emerald-600">
                    <CheckCircle2 size={12} /> Mobile number verified
                  </p>
                )}
                {phoneStatus === "pending" && (
                  <p className="mt-1.5 text-xs text-slate-400">OTP sent — check your phone</p>
                )}
                {phoneStatus === "error" && (
                  <p className="mt-1.5 text-xs text-red-500">Could not send OTP. Please try again.</p>
                )}
                {phoneStatus !== "verified" && phoneFormatError && (
                  <p className="mt-1.5 text-xs text-red-500">{phoneFormatError}</p>
                )}
              </div>

              <div>
                <label htmlFor="contact-email-input" className={labelCls}>
                  Email
                </label>
                <input
                  id="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.com"
                  required
                  data-testid="contact-email-input"
                  className={`${inputCls} ${emailStatus === "invalid" ? "border-red-500" : emailStatus === "valid" ? "border-emerald-500" : ""}`}
                />
                {emailStatus === "verifying" && (
                  <p className="mt-1.5 flex items-center gap-1 text-xs text-slate-400">
                    <Loader2 size={12} className="animate-spin" /> Verifying…
                  </p>
                )}
                {emailStatus === "invalid" && <p className="mt-1.5 text-xs text-red-500">{emailError}</p>}
                {emailStatus === "valid" && (
                  <p className="mt-1.5 flex items-center gap-1 text-xs text-emerald-600">
                    <CheckCircle2 size={12} /> Email is valid
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="contact-subject-input" className={labelCls}>
                  Subject
                </label>
                <input
                  id="contact-subject-input"
                  type="text"
                  value={form.subject}
                  onChange={update("subject")}
                  placeholder="How can we help?"
                  required
                  data-testid="contact-subject-input"
                  className={inputCls}
                />
              </div>

              <div>
                <label htmlFor="contact-message-input" className={labelCls}>
                  Message
                </label>
                <textarea
                  id="contact-message-input"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us a little about your enquiry..."
                  rows={5}
                  data-testid="contact-message-input"
                  className={`${inputCls} resize-none`}
                />
              </div>

              <button
                type="submit"
                disabled={!canSubmit || submitting}
                data-testid="contact-submit-button"
                className="group w-full inline-flex items-center justify-center gap-3 rounded-lg bg-miti-blue px-7 py-4 text-sm font-semibold text-white hover:bg-[#005a96] transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send size={18} /> Send my message
                  </>
                )}
              </button>
              {!canSubmit && (
                <p className="text-xs text-slate-400 text-center">
                  {!isEmailValid && !isPhoneVerified
                    ? "Enter a valid email and verify your mobile number to enable sending."
                    : !isEmailValid
                      ? "Enter a valid email above to enable sending."
                      : "Verify your mobile number to enable sending."}
                </p>
              )}
            </form>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {showOtpModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              data-testid="contact-otp-modal"
              className="relative w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-2xl"
            >
              <button
                type="button"
                onClick={() => setShowOtpModal(false)}
                data-testid="contact-otp-close-button"
                className="absolute right-4 top-4 text-slate-400 transition-colors hover:text-slate-700"
              >
                <X size={18} />
              </button>

              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-miti-blue/10 text-miti-blue">
                  <KeyRound size={18} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-slate-900">Verify Mobile Number</h3>
                  <p className="text-xs text-slate-500">
                    Enter the 4-digit OTP sent to {selectedCountry.dial} {form.phone}
                  </p>
                </div>
              </div>

              <input
                autoFocus
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={otpValue}
                onChange={(e) => {
                  setOtpValue(e.target.value.replace(/\D/g, ""));
                  setOtpError("");
                }}
                placeholder="_ _ _ _"
                data-testid="contact-otp-input"
                className="mb-3 w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] text-slate-900 outline-none transition-colors focus:border-miti-blue"
              />

              {otpError && <p className="mb-3 text-xs text-red-500">{otpError}</p>}

              <button
                type="button"
                onClick={verifyOtp}
                disabled={otpValue.length !== 4}
                data-testid="contact-otp-verify-button"
                className="w-full rounded-full bg-miti-blue py-3 text-sm font-bold text-white transition-colors hover:bg-[#005a96] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Verify OTP
              </button>

              <button
                type="button"
                onClick={sendOtp}
                disabled={phoneStatus === "sending"}
                data-testid="contact-otp-resend-button"
                className="mt-3 w-full text-center text-xs text-slate-400 transition-colors hover:text-miti-blue disabled:opacity-40"
              >
                {phoneStatus === "sending" ? "Sending…" : "Resend OTP"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ContactUs;
